import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  Button,
  IconButton,
  Divider,
  Paper,
  Autocomplete,
  InputLabel,
  TextField,
  InputAdornment
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ReplyIcon from "@mui/icons-material/Reply";
import PropTypes from "prop-types";
import { RenderContentPwc } from "./RenderContentPwc";

export const RateFringeCalculation = ({
  renderData,
  searchInput,
  jobSelection,
  jobTitleOptionsArray,
  open,
  handleClose,
  handleChatbotSubmit,
  resetPWC,
  additionalRateQuestion,
  setAnswerString,
  ansAdditionalRateQuestion,
  isQueValidate,
  source
}) => {
  const getCommaValue = arrList => {
    if (Array.isArray(arrList)) {
      let stringComma = "";
      arrList.forEach((item, index) => {
        if (index === 0) {
          stringComma = item;
        } else {
          stringComma = `${stringComma}, ${item}`;
        }
      });
      return stringComma;
    }
    return "";
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Rate & Fringe Calculation
        <IconButton aria-label="close" onClick={handleClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Grid container spacing={2} sx={{ pl: 2 }}>
          <Paper variant="outlined" sx={{ p: 0, display: "flex", mt: 2, width: "100%" }} flexDirection="row">
            {/* Left Panel */}
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 2, backgroundColor: "#e8f7f9", mr: 2, height: "100%" }}>
                <Box mb={2}>
                  <Typography variant="subtitle2">
                    <strong>State</strong>
                  </Typography>
                  <Typography>{searchInput.wsPayload.state}</Typography>
                </Box>

                <Box mb={2}>
                  <Typography variant="subtitle2">
                    <strong>County</strong>
                  </Typography>
                  <Typography>{getCommaValue(searchInput.wsPayload.county)}</Typography>
                </Box>

                <Box mb={2}>
                  <Typography variant="subtitle2">
                    <strong>Category</strong>
                  </Typography>
                  <Typography>{searchInput.constructionType}</Typography>
                </Box>

                <Box mb={2}>
                  <Typography variant="subtitle2">
                    <strong>{searchInput?.values?.isExternalJobTitle ? "External Labor Classification Title" : "Labor Classification Title"}</strong>
                  </Typography>
                  <Typography>{searchInput.jobTitlePW}</Typography>
                </Box>

                <Box mb={2}>
                  <Typography variant="subtitle2">
                    <strong>Labor Classification Description</strong>
                  </Typography>
                  <Typography>{searchInput?.values?.jobDescription}</Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2">
                    <strong>Labor Classification Position</strong>
                  </Typography>
                  <Typography>
                    {getCommaValue(searchInput?.values?.isOperator ? searchInput?.values?.typeOfEquipment : searchInput?.values?.typeOfJob)}
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Right Panel */}
            <Grid item xs={12} md={8} sx={{ p: 2 }}>
              {jobTitleOptionsArray?.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Grid container spacing={2}>
                    {jobTitleOptionsArray?.map((field, index) => (
                      <Grid item xs={12} md={12} key={Number(index)}>
                        <Autocomplete
                          id={index}
                          name={index}
                          options={field?.jobTitleOptions || []}
                          getOptionLabel={option => option.label}
                          getOptionValue={option => option.value}
                          value={field.jobTitle}
                          readOnly={jobTitleOptionsArray?.length !== index + 1 || renderData.chatBotRates}
                          onChange={(event, newValue) => jobSelection(index, field, newValue)}
                          renderInput={params => (
                            <>
                              <InputLabel shrink sx={{ wordBreak: "break-word", whiteSpace: "normal", overflow: "visible", width: "100%" }}>
                                {`${index + 1}. ${field.jobTitleLabel}`}
                              </InputLabel>
                              <TextField {...params} variant="outlined" fullWidth />
                            </>
                          )}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
              {additionalRateQuestion?.length > 0 && (
                <>
                  <Box sx={{ mt: 3, mb: 3 }}>
                    <Grid container spacing={2}>
                      {additionalRateQuestion?.map((question, index) => (
                        <Grid item xs={12} md={12} key={index + 1}>
                          <InputLabel shrink sx={{ wordBreak: "break-word", whiteSpace: "normal", overflow: "visible", width: "100%" }}>
                            {`${index + 1}. ${question.question}`}
                          </InputLabel>
                          <TextField
                            fullWidth
                            label="Answer"
                            id="fullWidth"
                            type="number"
                            disabled={additionalRateQuestion?.length !== index + 1 || renderData.chatBotRates}
                            onChange={event => {
                              setAnswerString(event.target.value);
                            }}
                            InputProps={{
                              endAdornment: additionalRateQuestion?.length - 1 === index && (
                                <InputAdornment position="end" className="icon-hor-vert">
                                  <IconButton
                                    onClick={ansAdditionalRateQuestion}
                                    edge="end"
                                    disabled={additionalRateQuestion?.length !== index + 1 || renderData.chatBotRates}
                                  >
                                    <ReplyIcon />
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                          />
                          {isQueValidate && additionalRateQuestion?.length - 1 === index && (
                            <Typography sx={{ fontSize: "12px !important", color: "#d32f2f" }}>{"Please answer the question"}</Typography>
                          )}
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                </>
              )}
              <RenderContentPwc
                isWagesLoader={renderData.isWagesLoader}
                chatBotFringes={renderData.chatBotFringes}
                chatBotRates={renderData.chatBotRates}
                isNoStateOrCountyData={renderData.isNoStateOrCountyData}
                errorMsg={renderData.errorMsg}
                footNotes={renderData.footNotes}
                chatBotMatchScore={renderData.chatBotMatchScore}
                source={source}
              />
            </Grid>
          </Paper>
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Box display="flex" justifyContent="flex-end" my={1}>
          <Button onClick={handleClose} variant="outlined" sx={{ mr: 2 }}>
            Cancel
          </Button>
          <Button onClick={resetPWC} variant="contained" sx={{ mr: 2 }}>
            Reset
          </Button>
          <Button onClick={handleChatbotSubmit} variant="contained" sx={{ mr: 2 }} autoFocus>
            Next
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
RateFringeCalculation.propTypes = {
  renderData: PropTypes.object,
  searchInput: PropTypes.object,
  jobSelection: PropTypes.func.isRequired,
  jobTitleOptionsArray: PropTypes.array,
  open: PropTypes.bool,
  handleClose: PropTypes.func.isRequired,
  handleChatbotSubmit: PropTypes.func.isRequired,
  resetPWC: PropTypes.func.isRequired,
  additionalRateQuestion: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.string]).isRequired,
  setAnswerString: PropTypes.any.isRequired,
  isQueValidate: PropTypes.any.isRequired,
  ansAdditionalRateQuestion: PropTypes.any.isRequired
};
